import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';

type PricingModel = 'free' | 'one_time' | 'subscription';

interface PublishPayload {
  name: string;
  description: string;
  category: string;
  pricing_model: PricingModel;
  price_usd?: number;
  solution_url: string;
  documentation_url: string;
}

const CATEGORIES = [
  'THEO Agents',
  'Automation',
  'Security',
  'Monitoring',
  'Reporting',
  'Integration',
  'Communication',
  'Data & Analytics',
  'Other',
];

const STEPS = ['Details', 'Pricing', 'Solution', 'Review'];

export function MarketplacePublish() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [pricingModel, setPricingModel] = useState<PricingModel>('free');
  const [priceUsd, setPriceUsd] = useState('');
  const [solutionUrl, setSolutionUrl] = useState('');
  const [documentationUrl, setDocumentationUrl] = useState('');

  const publishMutation = useMutation({
    mutationFn: (payload: PublishPayload) =>
      api('marketplace/listings', { method: 'POST', body: payload }),
  });

  function canAdvance(): boolean {
    switch (step) {
      case 0:
        return name.trim().length > 0 && description.trim().length > 0 && category.length > 0;
      case 1:
        return pricingModel === 'free' || (!!priceUsd && parseFloat(priceUsd) > 0);
      case 2:
        return solutionUrl.trim().length > 0;
      default:
        return true;
    }
  }

  function handleSubmit() {
    const payload: PublishPayload = {
      name: name.trim(),
      description: description.trim(),
      category,
      pricing_model: pricingModel,
      solution_url: solutionUrl.trim(),
      documentation_url: documentationUrl.trim(),
    };
    if (pricingModel !== 'free') {
      payload.price_usd = parseFloat(priceUsd);
    }
    publishMutation.mutate(payload);
  }

  // Success state
  if (publishMutation.isSuccess) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="rounded-lg border bg-card p-12 text-center">
          <div className="h-12 w-12 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4">
            <svg className="h-6 w-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Listing Submitted</h2>
          <p className="text-muted-foreground mb-6">
            Your solution has been submitted for review. You will be notified once it is approved.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/marketplace/my-listings')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
            >
              View My Listings
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-4 py-2 rounded-md text-sm font-medium border hover:bg-accent transition-colors"
            >
              Back to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-1">Publish Solution</h1>
        <p className="text-muted-foreground">Share your AI solution with the MSP community.</p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className="flex items-center gap-2 flex-1">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                  i < step
                    ? 'bg-primary text-primary-foreground'
                    : i === step
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                }`}
              >
                {i < step ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-xs hidden sm:block ${
                  i <= step ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px flex-1 ${i < step ? 'bg-primary' : 'bg-muted'}`} />
            )}
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-card p-6">
        {/* Step 1: Details */}
        {step === 0 && (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold text-foreground">Solution Details</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My AI Solution"
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
                placeholder="Describe what your solution does, who it's for, and how it helps..."
                className="w-full border rounded-md px-3 py-2 bg-background text-sm resize-y"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Pricing */}
        {step === 1 && (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold text-foreground">Pricing</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Pricing Model</label>
              <div className="grid grid-cols-3 gap-3">
                {([
                  { value: 'free', label: 'Free', desc: 'No charge' },
                  { value: 'one_time', label: 'One-Time', desc: 'Single purchase' },
                  { value: 'subscription', label: 'Subscription', desc: 'Monthly recurring' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPricingModel(opt.value)}
                    className={`rounded-lg border p-4 text-left transition-colors ${
                      pricingModel === opt.value
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-primary/50'
                    }`}
                  >
                    <p className="text-sm font-medium text-foreground">{opt.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            {pricingModel !== 'free' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Price (USD){pricingModel === 'subscription' ? ' / month' : ''}
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={priceUsd}
                  onChange={(e) => setPriceUsd(e.target.value)}
                  placeholder="9.99"
                  className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                />
              </div>
            )}
          </div>
        )}

        {/* Step 3: Solution */}
        {step === 2 && (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold text-foreground">Solution Files</h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Solution URL</label>
              <input
                type="url"
                value={solutionUrl}
                onChange={(e) => setSolutionUrl(e.target.value)}
                placeholder="https://github.com/your-org/your-solution"
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Link to your solution package, repository, or downloadable archive.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Documentation URL <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={documentationUrl}
                onChange={(e) => setDocumentationUrl(e.target.value)}
                placeholder="https://docs.example.com/my-solution"
                className="w-full border rounded-md px-3 py-2 bg-background text-sm"
              />
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 3 && (
          <div className="space-y-5">
            <h3 className="text-lg font-semibold text-foreground">Review & Submit</h3>
            <p className="text-sm text-muted-foreground">
              Review your listing details before submitting.
            </p>
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span className="text-foreground font-medium">{name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category</span>
                <span className="text-foreground">{category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pricing</span>
                <span className="text-foreground">
                  {pricingModel === 'free'
                    ? 'Free'
                    : pricingModel === 'subscription'
                      ? `$${priceUsd}/mo`
                      : `$${priceUsd} one-time`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Solution URL</span>
                <span className="text-foreground truncate ml-4 max-w-[300px]">{solutionUrl}</span>
              </div>
              {documentationUrl && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Docs URL</span>
                  <span className="text-foreground truncate ml-4 max-w-[300px]">{documentationUrl}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Description</span>
                <p className="text-foreground mt-1 whitespace-pre-wrap">{description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {publishMutation.isError && (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-500 mt-4">
            {publishMutation.error instanceof Error ? publishMutation.error.message : 'Failed to publish. Please try again.'}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={() => (step === 0 ? navigate('/marketplace') : setStep(step - 1))}
            className="px-4 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={publishMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {publishMutation.isPending ? 'Publishing...' : 'Publish Solution'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
